import { Configuration } from '@reliance/types/models/Configuration';
import { Facility } from '@reliance/types/models/Facility';
import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import { alertStore } from '../alert/alertStore';
import api from '../common/api';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import { IStatusResponse } from '../common/reliance-api-service';
import { useFacilityConfigurations } from '../hooks/Configuration';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import FacilityConfigurationModal from '../configuration/FacilityConfigurationModal';

type FacilityConfigTabProps = {
  facility: Facility;
};

const FacilityConfigTab: React.FC<FacilityConfigTabProps> = ({ facility }) => {
  const [filterText, setFilterText] = useState('');
  const [modalRow, setModalRow] = useState<Configuration | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Configuration | null>(null);

  const listResult = useFacilityConfigurations(facility.id);

  useNotifyTabLoading('config', listResult.loading);

  useEffect(() => {
    if (listResult.error) {
      alertStore.error('An unexpected error occurred while loading config');
    }
  }, [listResult.error]);

  const rows = useMemo(() => {
    const data = listResult.result ?? [];
    const sorted = [...data].sort((a, b) => String(a.key).localeCompare(String(b.key)));
    const q = filterText.trim().toLowerCase();
    if (!q) {
      return sorted;
    }
    return sorted.filter(
      (row) =>
        String(row.id).includes(q) ||
        String(row.key).toLowerCase().includes(q) ||
        String(row.value).toLowerCase().includes(q)
    );
  }, [listResult.result, filterText]);

  const closeModal = (success?: boolean) => {
    setModalRow(undefined);
    if (success) {
      void listResult.execute(facility.id);
    }
  };

  const performDelete = async (row: Configuration) => {
    const { data } = await api.delete<IStatusResponse>(`/configuration/${row.id}`);
    if (!data.success) {
      alertStore.error('An unexpected error occurred');
      return;
    }
    alertStore.success('Configuration deleted successfully', true);
    if (modalRow != null && modalRow?.id === row.id) {
      setModalRow(undefined);
    }
    void listResult.execute(facility.id);
  };

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <div className="row mb-2">
          <div className="col-md-12">
            <Button variant="primary" className="btn-labeled me-2" onClick={() => setModalRow(null)}>
              <span className="btn-label">
                <i className="fas fa-plus-circle" />
              </span>
              Add Facility Config
            </Button>
            <FormControl
              type="search"
              placeholder="Filter…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{ maxWidth: 280, display: 'inline-block' }}
              className="ms-2"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            {listResult.loading && rows.length === 0 ? (
              <p className="text-muted">Loading…</p>
            ) : (
              <table className="table table-striped table-hover table-bordered">
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Key</th>
                    <th>Value</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.key}</td>
                      <td style={{ wordBreak: 'break-all' }}>{row.value}</td>
                      <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>
                        <div className="d-flex flex-nowrap align-items-center">
                          <Button variant="secondary" size="sm" className="me-1" onClick={() => setModalRow(row)}>
                            View
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            title="Delete"
                            aria-label={`Delete configuration ${row.key}`}
                            onClick={() => setDeleteTarget(row)}>
                            <i className="fas fa-trash-alt" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {modalRow !== undefined && (
        <FacilityConfigurationModal facilityId={facility.id} configuration={modalRow ?? undefined} onClose={closeModal} />
      )}
      {deleteTarget != null && (
        <ConfirmModal
          title="Delete configuration"
          show
          acceptText="Delete"
          rejectText="Cancel"
          onClose={() => setDeleteTarget(null)}
          onAccept={async () => {
            try {
              await performDelete(deleteTarget);
            } catch {
              alertStore.error('Configuration failed to be deleted', true);
            }
          }}>
          <p>
            Are you sure you want to delete configuration <strong>{deleteTarget.key}</strong>?
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default FacilityConfigTab;
